import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  resource,
  signal,
  viewChild,
} from '@angular/core';
import {
  DsButtonModule,
  DsHeaderComponent,
  DsHeaderLocaleConfigOption,
  DsHeaderModule,
  DsHeaderUserInfo,
  DsImprintModule,
  DsListItemModule,
  DsModalModule,
  DsThemeService,
} from '@bmw-ds/components';

import { AdminModeService } from '../../../auth/admin-mode/admin-mode.service';
import { AuthenticationService } from '../../../auth/authentication/authentication.service';
import { ReadonlyService } from '../../../auth/readonly/readonly.service';
import { LegalEntity } from '../../../auth/user/legal-entities.model';
import { UserService } from '../../../auth/user/user.service';
import { I18nService, useI18n } from '../../i18n/services/i18n.service';
import { AdminControlContainer } from './admin-control/admin-control.container';
import { AppInfoComponent } from './app-info/app-info.component';
import { ImprintModalWrapper } from './imprint-modal.wrapper';

@Component({
  selector: 'cdf-header',
  imports: [
    DsHeaderModule,
    DsModalModule,
    DsImprintModule,
    ImprintModalWrapper,
    AdminControlContainer,
    DsListItemModule,
    DsButtonModule,
    AppInfoComponent,
  ],
  template: `
    <ds-header
      [appName]="i18n('appName')"
      [environmentTag]="environmentTag()"
      [localeOptions]="localeOptions"
      [userInfo]="userInfo()"
      [menuTemplate]="customMenu"
      [localeID]="i18nService.activeLanguage()"
      (localeIDChange)="$event && i18nService.setUiLanguage($event)"
      [guardImprint]="true"
      [labellingConfig]="{ logout: i18n('logout') }"
      (requestLogout)="authService.logOut()"
      (requestImprintOpen)="imprintOpen.set(true)"
    />

    <ng-template #customMenu>
      <cdf-app-info />
      @if (userInfo() === undefined || adminModeService.hasAdminReadPermissions()) {
        <hr class="my-2x mx-n2x hr-bg" />
      }

      @if (userInfo() === undefined) {
        <ds-button (click)="authService.logOut()">{{ i18n('logout') }}</ds-button>
      }

      @if (adminModeService.hasAdminReadPermissions()) {
        <cdf-admin-control-container
          [warnReturnToStart]="adminModeWarnReturnToStart()"
          (returnToStartPage)="returnToStartPage.emit()"
          (closeMenu)="closeMenu()"
        />
      }
    </ng-template>

    <!--
      This for loop and guardImprint are needed because of bug DESY-4212,
      where the imprint language does not otherwise support dynamic language choice.

      The workaround is a for loop with a tracker that mirrors the DS-Imprint’s language logic,
      so that the cdf-imprint-modal is destroyed and recreated.
      That component can then provide the latest locale ID.
    -->
    @for (language of [i18nService.activeLanguage()]; track language.startsWith('de')) {
      <cdf-imprint-modal [(imprintOpen)]="imprintOpen" />
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: 'header.container.scss',
})
export class HeaderContainer implements OnInit {
  protected readonly i18nService = inject(I18nService);
  protected readonly adminModeService = inject(AdminModeService);
  protected readonly readonlyService = inject(ReadonlyService);
  protected readonly themeService = inject(DsThemeService);
  protected readonly i18n = useI18n();
  protected readonly authService = inject(AuthenticationService);
  protected readonly userService = inject(UserService);

  readonly adminModeWarnReturnToStart = input(false);
  readonly returnToStartPage = output<void>();

  readonly header = viewChild(DsHeaderComponent);

  protected readonly imprintOpen = signal(false);
  protected readonly userInfo = computed(() =>
    this.userInfoResource.hasValue() ? this.userInfoResource.value() : undefined,
  );
  private readonly userInfoResource = resource({
    loader: async (): Promise<Partial<DsHeaderUserInfo>> => {
      const user = await this.userService.user();
      return {
        username: `${user.firstName} ${user.lastName}`,
        department: `${user.distributionPartnerName} (${user.distributionPartnerNumber}\u00A0/\u00A0${user.activeBusinessPartnerId}) · ${user.subject}`,
      };
    },
  });

  ngOnInit() {
    this.themeService.theme = 'light'; // switch back to light theme if dark mode was active
  }

  protected readonly localeOptions: DsHeaderLocaleConfigOption[] = I18nService.supportedLocales.map((lang) => ({
    id: lang.id,
    label: lang.shortCode,
    language: lang.localName,
  }));

  protected readonly environmentTag = computed(() => {
    if (!this.userService.doneLoading()) {
      return { label: undefined };
    } else if (this.adminModeService.isAdminModeActive()) {
      const entity = this.getActiveAdminLegalEntity();
      const entityString = `${entity.dpNr} ${entity.name}`;
      if (this.readonlyService.readonly()) {
        return {
          label: `${this.i18n('adminMode.adminModeActiveHint')}, ${this.i18n('readonly')}: ${entityString}`,
          tone: 'caution',
        };
      } else {
        return { label: `${this.i18n('adminMode.adminModeActiveHint')}: ${entityString}`, tone: 'caution' };
      }
    } else if (this.readonlyService.readonly()) {
      return { label: this.i18n('readonly'), tone: 'caution' };
    }
    return { label: undefined };
  });

  private getActiveAdminLegalEntity(): LegalEntity {
    const country = this.adminModeService.activeAdminCountry();
    if (country) {
      const foundEntity = this.userService
        .legalEntitiesForAdminMode()
        .get(country)
        ?.find((entity) => entity.dpNr === this.adminModeService.activeAdminDpNr());
      if (foundEntity) {
        return foundEntity;
      }
    }
    return { name: '', dpNr: '' };
  }

  closeMenu() {
    this.header()?.menuRef?.handleCloseClick();
  }
}
